import { BillingStrategy, type AddOnsVO, type PlanGroup } from '@ctct/domain-layer';
import { AccountModificationFlowId } from '@ctct/infrastructure-layer';
import { ProgressBarStepId } from '@ctct/state-client-layer';

import type { TEventSource } from '../../../helpers';
import type { TranslationGetter } from '../../../types';
import { payflowProgressBarEventBus } from '../PayflowProgressBarEventBus';
import type { Flags, ProgressbarPageStore } from '../types';

type Step = {
  id: ProgressBarStepId;
  label: string;
  state: 'pending' | 'current' | 'finished' | 'visited';
};

export class PayflowProgressBarService {
  constructor(
    private pageStore: ProgressbarPageStore,
    private planGroup: PlanGroup,
    private addOns: AddOnsVO,
    private flags: Flags,
    private t: TranslationGetter,
    private billingStrategy: BillingStrategy,
    private stepsHistory: {
      addVisitedStep: (id: ProgressBarStepId) => void;
      isVisitedStep: (id: ProgressBarStepId) => boolean;
      isFinishedStep: (id: ProgressBarStepId) => boolean;
      isVisitedNotFinishedStep: (id: ProgressBarStepId) => boolean;
    },
    private flowId: AccountModificationFlowId
  ) {}

  public api() {
    return {
      hideBar: () => this.pageStore.setState({ isProgressBarHidden: true }),
      showBar: () => this.pageStore.setState({ isProgressBarHidden: false }),

      getActiveStep: () => this.pageStore.getState().activeStepId,
      getAllSteps: this.getAllStepsForFlow,

      goToPlans: () => this.goToStep(ProgressBarStepId.SELECT_PLAN, 'api'),
      goToAddOns: () => {
        if (!this.getShouldHideAddOnsStep()) {
          this.goToStep(ProgressBarStepId.ADD_ONS, 'api');
        } else {
          this.goToStep(ProgressBarStepId.CHECKOUT, 'api');
        }
      },

      goToPaymentPlans: () => this.goToStep(ProgressBarStepId.PAYMENT_PLAN, 'api'),

      goToPaymentPlanSelect: () => this.goToStep(ProgressBarStepId.PAYMENT_PLAN_SELECT, 'api'),

      goToCheckout: () => this.goToStep(ProgressBarStepId.CHECKOUT, 'api'),

      goToNextStep: () => this.goToNextStep('api'),

      setStepToPlans: () => this.setActiveStep(ProgressBarStepId.SELECT_PLAN),
      setStepToAddOns: () => {
        if (!this.getShouldHideAddOnsStep()) {
          this.setActiveStep(ProgressBarStepId.ADD_ONS);
        } else {
          this.setActiveStep(ProgressBarStepId.CHECKOUT);
        }
      },
      setStepToCheckout: () => this.setActiveStep(ProgressBarStepId.CHECKOUT),
      getFlowId: () => this.flowId,
    };
  }

  public data() {
    return {
      getSteps: this.getStepsWithValidStatuses,
      getIsMWebVariantEnabled: () => this.flags?.isMWebVariantEnabled,
      getIsValueFocusedMWebPlanSelectionEnabled: () => this.flags?.isValueFocusedMWebPlanSelectionEnabled,
    };
  }

  public handlers() {
    return {
      onStepClick: (stepId: ProgressBarStepId) => {
        if (this.stepsHistory.isVisitedStep(stepId)) {
          this.goToStep(stepId, 'user');
        }
      },
    };
  }

  public onMount = () => {
    const { isSecureCheckout } = this.flags;

    if (isSecureCheckout) {
      switch (this.flowId) {
        case AccountModificationFlowId.ChangePaymentPlan:
          this.stepsHistory.addVisitedStep(ProgressBarStepId.PAYMENT_PLAN_SELECT);
          this.stepsHistory.addVisitedStep(ProgressBarStepId.CHECKOUT);
          break;

        case AccountModificationFlowId.Purchase:
        default:
          this.stepsHistory.addVisitedStep(ProgressBarStepId.SELECT_PLAN);
          this.stepsHistory.addVisitedStep(ProgressBarStepId.ADD_ONS);
          this.stepsHistory.addVisitedStep(ProgressBarStepId.PAYMENT_PLAN);
          this.stepsHistory.addVisitedStep(ProgressBarStepId.CHECKOUT);
          break;
      }

      this.pageStore.setState({ activeStepId: ProgressBarStepId.CHECKOUT });

      return;
    }

    const firstStepId = this.getAllStepsForFlow()[0]?.id;

    if (firstStepId) {
      this.stepsHistory.addVisitedStep(firstStepId);
      this.pageStore.setState({ activeStepId: firstStepId });
    }
  };

  // ----------------------------------------------------
  // Private methods
  // ----------------------------------------------------
  private goToNextStep = (source: TEventSource) => {
    const allSteps = this.getAllStepsForFlow();
    const { activeStepId } = this.pageStore.getState();

    const index = allSteps.findIndex((s) => s.id === activeStepId);

    if (index === -1) return;

    const nextStep = allSteps[index + 1];

    if (nextStep) {
      this.goToStep(nextStep.id, source);
    }
  };

  private goToStep = (stepId: ProgressBarStepId, source: TEventSource) => {
    this.setActiveStep(stepId);
    payflowProgressBarEventBus.emit('goToStep', stepId, this.flowId, { source });
  };

  private setActiveStep = (stepId: ProgressBarStepId) => {
    const allSteps = this.getAllStepsForFlow();

    const index = allSteps.findIndex((s) => s.id === stepId);

    if (index === -1) {
      // If active step exists, do nothing
      if (this.pageStore.getState().activeStepId) return;

      // If step is not found, go to the first step
      this.setActiveStep(allSteps[0].id);

      return;
    }

    this.pageStore.setState({ activeStepId: stepId });

    const stepsBefore = allSteps.slice(0, index);

    stepsBefore.forEach((s) => this.stepsHistory.addVisitedStep(s.id));

    this.stepsHistory.addVisitedStep(stepId);
  };

  private getAllStepsForFlow = () => {
    const StepSelectPlan: Step = {
      id: ProgressBarStepId.SELECT_PLAN,
      label: this.t('application-layer.progressbar.steps.select-plan'),
      state: 'pending',
    };

    const StepAddOns: Step = {
      id: ProgressBarStepId.ADD_ONS,
      label: this.t('application-layer.progressbar.steps.add-ons'),
      state: 'pending',
    };

    const StepPaymentPlan: Step = {
      id: ProgressBarStepId.PAYMENT_PLAN,
      label: this.t('application-layer.progressbar.steps.payment-plan'),
      state: 'pending',
    };

    const StepPaymentPlanSelect: Step = {
      id: ProgressBarStepId.PAYMENT_PLAN_SELECT,
      label: this.t('application-layer.progressbar.steps.payment-plan'),
      state: 'pending',
    };

    const StepCheckout: Step = {
      id: ProgressBarStepId.CHECKOUT,
      label: this.t('application-layer.progressbar.steps.checkout'),
      state: 'pending',
    };

    if (this.flowId === AccountModificationFlowId.ChangePaymentPlan) {
      return [StepPaymentPlanSelect, StepCheckout];
    }

    const customerSteps = [
      StepSelectPlan,
      ...(this.billingStrategy === BillingStrategy.InHouse && this.flowId === AccountModificationFlowId.Purchase
        ? [StepPaymentPlan]
        : []),
      StepCheckout,
    ];
    const trialerSteps = [StepSelectPlan, ...(this.getShouldHideAddOnsStep() ? [] : [StepAddOns]), StepCheckout];

    return this.flags.isCustomerFlow || this.flags.isSuspended ? customerSteps : trialerSteps;
  };

  private getStepsWithValidStatuses = (activeStepId: ProgressBarStepId | null): Step[] => {
    return this.getAllStepsForFlow().map((step) => ({
      ...step,
      state: this.getStepState(step.id, activeStepId),
    }));
  };

  private getStepState = (stepId: ProgressBarStepId, activeStepId: ProgressBarStepId | null): Step['state'] => {
    if (stepId === activeStepId || this.stepsHistory.isVisitedNotFinishedStep(stepId)) return 'current';

    if (this.stepsHistory.isFinishedStep(stepId)) return 'finished';

    if (this.stepsHistory.isVisitedStep(stepId)) return 'visited';

    return 'pending';
  };

  private getShouldHideAddOnsStep = () => {
    const isCustomer = this.planGroup.isCustomer();
    const hasAddOns = this.addOns.getAddOns().length === 0;
    // For SocialStarter trialers we want to skip the add-ons step entirely. Currently BE reponses with IBP and SMS addon for SocialStarter plan group, which is why we have to add check on FE.
    const isSocialStarter = this.planGroup.isSocialStarter();
    const hideAddons = this.flags.hideAddons ?? false;

    return isCustomer || hasAddOns || isSocialStarter || hideAddons;
  };
}
